import { Box, CardContent, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { alpha } from '@mui/system';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { listOrganisationDailyUpdates } from '../../../../redux/slices/daily-updates';
import { RoleName } from '../../../../types/user';
import { StatusKey, DocumentStatusKey } from '../../../../types/daily-updates';
import {
	ModernCard,
	CardHeader,
	ProgressContainer,
	ModernProgress,
	COLORS,
	STATUS_COLORS,
	StatusTile,
	StatusCount,
	keyMapping
} from '../../../../pages/Organisation/DailyUpdates/utils';
import AdmissionsOfficerSkeleton from '../../../../components/ui/atoms/DailyUpdates/AdmissionsOfficerSkeleton';

const CustomStatusBadge = ({ color, label }: { color: string; label: number | string }) => (
	<Box
		sx={{
			borderRadius: '6px',
			backgroundColor: alpha(color, 0.15),
			color: color,
			fontWeight: 600,
			fontSize: '0.75rem',
			height: '24px',
			padding: '0 8px',
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center'
		}}
	>
		{label}
	</Box>
);

const AdmissionsOfficerDU: React.FC = () => {
	const dispatch = useAppDispatch();

	const [organisation, user] = useAppSelector((state) => [state.organisation.current, state.user.currentUser]);
	const admissionsOfficerDU = useAppSelector(
		(state) => state.dailyUpdates.dailyUpdates?.[RoleName.AdmissionsOfficer]
	);

	const organisationId = organisation?.id || user?.roles?.find((r) => r.organisationId)?.organisationId;

	const candidatesTotalCount =
		(admissionsOfficerDU?.candidateInterests?.completed ?? 0) +
		(admissionsOfficerDU?.candidateInterests?.inProgress ?? 0) +
		(admissionsOfficerDU?.candidateInterests?.pending ?? 0) +
		(admissionsOfficerDU?.candidateInterests?.dismissed ?? 0);
	const documentsTotalCount =
		(admissionsOfficerDU?.sharedDocuments?.completed ?? 0) +
		(admissionsOfficerDU?.sharedDocuments?.pending ?? 0) +
		(admissionsOfficerDU?.sharedDocuments?.dismissed ?? 0);

	useEffect(() => {
		organisationId &&
			user?.id &&
			dispatch(
				listOrganisationDailyUpdates({
					organisationId,
					roleName: RoleName.AdmissionsOfficer,
					status: null, // Doesn't matter for daily updates
					dailyUpdates: true,
					lastLoggedOut: localStorage.getItem(`lastLogout_${user?.id}`) || undefined
				})
			);
	}, [organisationId, user?.id]);

	if (!admissionsOfficerDU) {
		return <AdmissionsOfficerSkeleton />;
	}

	return (
		<Box
			sx={{
				p: 3,
				maxWidth: '1400px',
				margin: '0 auto',
				backgroundColor: alpha(COLORS.neutral, 0.5),
				borderRadius: '24px'
			}}
		>
			{/* Stats Overview Cards */}
			<Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
				{/* Candidate Enquiries Card */}
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							Candidate Enquiries
						</Typography>
						<Typography variant="body2" sx={{ color: COLORS.textLight }}>
							{candidatesTotalCount} total enquiries
						</Typography>
					</CardHeader>
					<CardContent sx={{ p: 3 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{(['dismissed', 'completed', 'inProgress', 'pending'] as StatusKey[]).map((key) => {
								const count = admissionsOfficerDU.candidateInterests[key as StatusKey];
								const label = keyMapping[key];
								return (
									<Box key={key}>
										<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
											<Typography variant="body2" sx={{ color: COLORS.textLight }}>
												{label}
											</Typography>
											<CustomStatusBadge color={STATUS_COLORS[key]} label={count} />
										</Box>
										<ProgressContainer>
											<ModernProgress
												variant="determinate"
												value={
													candidatesTotalCount > 0
														? (count / candidatesTotalCount || 0) * 100
														: 0
												}
												barColor={STATUS_COLORS[key]}
											/>
										</ProgressContainer>
									</Box>
								);
							})}
						</Box>
					</CardContent>
				</ModernCard>

				{/* Document Requests Card */}
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							Document Requests
						</Typography>
						<Typography variant="body2" sx={{ color: COLORS.textLight }}>
							{documentsTotalCount} total requests
						</Typography>
					</CardHeader>
					<CardContent sx={{ p: 3 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{(['dismissed', 'completed', 'pending'] as DocumentStatusKey[]).map((key) => {
								const count = admissionsOfficerDU.sharedDocuments[key as DocumentStatusKey];
								const label = keyMapping[key];
								return (
									<Box key={key}>
										<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
											<Typography variant="body2" sx={{ color: COLORS.textLight }}>
												{label}
											</Typography>
											<CustomStatusBadge color={STATUS_COLORS[key as StatusKey]} label={count} />
										</Box>
										<ProgressContainer>
											<ModernProgress
												variant="determinate"
												value={
													documentsTotalCount > 0
														? (count / documentsTotalCount || 0) * 100
														: 0
												}
												barColor={STATUS_COLORS[key as StatusKey]}
											/>
										</ProgressContainer>
									</Box>
								);
							})}
						</Box>
					</CardContent>
				</ModernCard>
			</Box>

			{/* Invite Status Updates */}
			<Box sx={{ mb: 4 }}>
				{/* Status Tiles */}
				<Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
					{/* Invites Sent Tile */}
					<StatusTile color={COLORS.primary} sx={{ flex: 1 }}>
						<Typography variant="body2" sx={{ color: COLORS.textLight, mb: 1 }}>
							Prospective Invites Sent
						</Typography>
						<StatusCount>{admissionsOfficerDU.noProspectives.pending ?? 0}</StatusCount>
					</StatusTile>

					<StatusTile color={COLORS.warning} sx={{ flex: 1 }}>
						<Typography variant="body2" sx={{ color: COLORS.textLight, mb: 1 }}>
							Prospective Invites Accepted
						</Typography>
						<StatusCount>{admissionsOfficerDU.noProspectives.confirmed ?? 0}</StatusCount>
					</StatusTile>

					{/* Invites Accepted Tile */}
					<StatusTile color={COLORS.success} sx={{ flex: 1 }}>
						<Typography variant="body2" sx={{ color: COLORS.textLight, mb: 1 }}>
							Prospective Invites Completed
						</Typography>
						<StatusCount>{admissionsOfficerDU.prospectiveStatusChanges ?? 0}</StatusCount>
					</StatusTile>
				</Box>
			</Box>
		</Box>
	);
};

export default AdmissionsOfficerDU;
